import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';
import { AppIconComponent } from '../../../../common/components/icon/icon.component';
import { BENEFICIARY_RELATIONSHIPS } from '../../../../core/data/constants/beneficiary-relationships';
import { QuoteBeneficiaryDto } from '../../../../core/data/dtos/quote-beneficiary.dto';
import { QuoteBeneficiaryUpsertDto } from '../../../../core/data/dtos/quote-beneficiary-upsert.dto';
import { beneficiaryPercentageTotal } from '../../../../common/utils/beneficiary-validators';
import { PASSPORT_MASK, passportNumberValidator } from '../../../../common/utils/document-validators';

/** Builds the form group for one policy beneficiary. */
export function buildBeneficiaryGroup(fb: FormBuilder, b?: QuoteBeneficiaryDto | null): FormGroup {
  return fb.group({
    id: [b?.id ?? null],
    name: [b?.name ?? null, [Validators.required, Validators.maxLength(40)]],
    lastName: [b?.lastName ?? null, [Validators.required, Validators.maxLength(40)]],
    documentType: [b?.documentType === 'Passport' ? 'Passport' : 'IdCard', [Validators.required]],
    documentNumber: [b?.documentNumber ?? null, [Validators.required]],
    relationship: [b?.relationship ?? null, [Validators.required]],
    percentage: [b?.percentage ?? null, [Validators.required, Validators.min(0.01), Validators.max(100)]],
  }, { validators: passportNumberValidator() });
}

/** Maps the beneficiaries FormArray to the API payload. */
export function mapBeneficiariesToUpsert(array: FormArray | null | undefined): QuoteBeneficiaryUpsertDto[] {
  if (!array) return [];
  return array.getRawValue().map((r: any, i: number) => ({
    id: r.id ?? null,
    name: (r.name ?? '').trim(),
    lastName: (r.lastName ?? '').trim(),
    documentType: r.documentType === 'Passport' ? 'Passport' : 'IdCard',
    documentNumber: (r.documentNumber ?? '').toString().trim(),
    relationship: r.relationship,
    percentage: Number(r.percentage ?? 0),
    sortOrder: i,
  }));
}

@Component({
  selector: 'app-checkout-beneficiaries',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgxMaskDirective, AppIconComponent],
  templateUrl: './checkout-beneficiaries.component.html',
})
export class CheckoutBeneficiariesComponent {
  @Input({ required: true }) formArray!: FormArray<FormGroup>;
  /** Unique prefix for input ids (one list per policy). */
  @Input() idPrefix = 'beneficiary';

  readonly relationships = BENEFICIARY_RELATIONSHIPS;

  constructor(private readonly fb: FormBuilder) {}

  get total(): number {
    return beneficiaryPercentageTotal(this.formArray);
  }

  get showArrayErrors(): boolean {
    return this.formArray.touched || this.formArray.controls.some((c) => c.touched);
  }

  documentMask(group: FormGroup): string {
    return group.get('documentType')?.value === 'Passport' ? PASSPORT_MASK : '000-0000000-0';
  }

  onDocumentTypeChange(group: FormGroup): void {
    group.get('documentNumber')?.setValue(null);
  }

  add(): void {
    this.formArray.push(buildBeneficiaryGroup(this.fb));
    this.formArray.markAsDirty();
  }

  remove(index: number): void {
    this.formArray.removeAt(index);
    this.formArray.markAsDirty();
    this.formArray.markAsTouched();
  }

  isInvalid(group: FormGroup, control: string): boolean {
    const c = group.get(control);
    return !!c && c.touched && c.invalid;
  }
}
